import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/adminApi';
import { ALL_DOMAINS, DOMAIN_LABELS, type Compound } from '@/lib/compounds';
import { DomainPill } from '@/components/DomainPill';
import { AdminPage, RegistryTable, EntityFormModal, type FieldSpec } from '@/admin';

const FIELDS: FieldSpec[] = [
  { name: 'canonical_name', label: 'Canonical Name', type: 'text', lockOnEdit: true, placeholder: 'e.g. magnesium_glycinate' },
  { name: 'aliases', label: 'Aliases (one per line)', type: 'lines', optional: true, hint: 'One alias per line' },
  { name: 'domains', label: 'Domains', type: 'checkboxGroup', optional: true, options: ALL_DOMAINS.map((d) => ({ value: d, label: DOMAIN_LABELS[d] ?? d })) },
];

export function CompoundsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['compounds'], queryFn: () => adminApi.compounds.list() });
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ editing: Compound | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['compounds'] });
  const save = useMutation({
    mutationFn: (v: Record<string, unknown>) => {
      const body = { canonical_name: String(v.canonical_name), aliases: (v.aliases as string[]) ?? [], domains: (v.domains as string[]) ?? [] };
      return modal?.editing
        ? adminApi.compounds.update(body.canonical_name, { aliases: body.aliases, domains: body.domains })
        : adminApi.compounds.create(body);
    },
    onSuccess: () => { setModal(null); setErr(null); void refresh(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Error'),
  });
  const del = useMutation({
    mutationFn: (name: string) => adminApi.compounds.remove(name),
    onSuccess: () => void refresh(),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error deleting compound'),
  });

  const rows = q.data ?? [];

  return (
    <AdminPage
      title="Compound Registry"
      description="Manage canonical compounds, aliases, and domain mappings"
      actions={<Button size="sm" onClick={() => { setErr(null); setModal({ editing: null }); }}>Add Compound</Button>}
    >
      <RegistryTable<Compound>
        columns={[
          { key: 'name', header: 'Canonical Name', className: 'font-mono text-[12px]', render: (c) => c.canonical_name },
          { key: 'aliases', header: 'Aliases', className: 'max-w-[300px] text-[12px] text-text-secondary', render: (c) => (c.aliases || []).join(', ') || '—' },
          { key: 'domains', header: 'Domains', render: (c) => (c.domains || []).length ? c.domains.map((d) => <DomainPill key={d} domain={d} />) : <span className="text-text-muted text-[12px]">—</span> },
        ]}
        rows={rows}
        getRowKey={(c) => c.canonical_name}
        count={q.data?.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No compounds match "${search}"` : 'No compounds registered'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by name or alias...' }}
        searchKeys={['canonical_name', 'aliases']}
        rowActions={(c) => (
          <>
            <Button variant="outline" size="sm" onClick={() => { setErr(null); setModal({ editing: c }); }}>Edit</Button>
            <Button variant="destructive" size="sm" onClick={() => { if (confirm(`Delete compound "${c.canonical_name}"? This cannot be undone.`)) del.mutate(c.canonical_name); }}>Delete</Button>
          </>
        )}
      />
      {modal ? (
        <EntityFormModal
          title={modal.editing ? 'Edit Compound' : 'Add Compound'}
          mode={modal.editing ? 'edit' : 'add'}
          fields={FIELDS}
          initialValues={{
            canonical_name: modal.editing?.canonical_name ?? '',
            aliases: modal.editing?.aliases ?? [],
            domains: modal.editing?.domains ?? [],
          }}
          error={err} busy={save.isPending}
          onClose={() => setModal(null)}
          onSubmit={(v) => save.mutate(v)}
        />
      ) : null}
    </AdminPage>
  );
}
